import BrainSearchAssistantMessage from "@/brain-search/internal/BrainSearchAssistantMessage"
import BrainSearchUserMessage from "@/brain-search/internal/BrainSearchUserMessage"
import { BrainSearchMessageListPaginationQuery } from "@/brain-search/internal/__generated__/BrainSearchMessageListPaginationQuery.graphql"
import {
  BrainSearchMessageList_PaginationFragment$data,
  BrainSearchMessageList_PaginationFragment$key,
} from "@/brain-search/internal/__generated__/BrainSearchMessageList_PaginationFragment.graphql"
import { GlobalID, NodeFromConnection } from "@/relay/RelayTypes"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoIcon, DiscoSpinner, DiscoText } from "@disco-ui"
import { useEffect, useRef } from "react"
import { graphql, usePaginationFragment } from "react-relay"

export type BrainSearchMessage = NodeFromConnection<
  NonNullable<BrainSearchMessageList_PaginationFragment$data["messages"]>
>

export const BRAIN_SEARCH_MESSAGES_PER_LOAD = 100

interface BrainSearchMessageListProps extends ActiveMessageContext {
  brainSearchKey: BrainSearchMessageList_PaginationFragment$key
  paddingBottom?: number
}

interface ActiveMessageContext {
  isBotThinking: boolean
  generatingMessageId: GlobalID | null
  onCreateMessage: (prompt: string, sourceRelatedQuestionId?: GlobalID | null) => void
}

function BrainSearchMessageList({
  brainSearchKey,
  isBotThinking,
  generatingMessageId,
  onCreateMessage,
  paddingBottom = 2,
}: BrainSearchMessageListProps) {
  const classes = useStyles({ paddingBottom })
  const listContainerRef = useRef<HTMLDivElement>(null)
  const activeSectionRef = useRef<HTMLDivElement>(null)
  const prevConversationIdRef = useRef<GlobalID | undefined>()
  const conversationScrollDoneRef = useRef<boolean>(false)

  const { data } = usePaginationFragment<
    BrainSearchMessageListPaginationQuery,
    BrainSearchMessageList_PaginationFragment$key
  >(
    graphql`
      fragment BrainSearchMessageList_PaginationFragment on BrainSearch
      @refetchable(queryName: "BrainSearchMessageListPaginationQuery")
      @argumentDefinitions(first: { type: "Int!" }, after: { type: "String" }) {
        id
        messages(first: $first, after: $after)
          @connection(key: "BrainSearchMessageList__messages") {
          totalCount
          edges {
            node {
              id
              type
              ...BrainSearchUserMessageFragment
              ...BrainSearchAssistantMessageFragment
            }
          }
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
    brainSearchKey
  )

  const messages = Relay.connectionToArray(data?.messages)

  let messageForActiveBlock1: BrainSearchMessage | undefined
  let messageForActiveBlock2: BrainSearchMessage | undefined
  let numMessagesInActiveBlock = 0

  if (messages.length > 0) {
    const lastMsg = messages[messages.length - 1]
    if (messages.length === 1) {
      messageForActiveBlock1 = lastMsg
      numMessagesInActiveBlock = 1
    } else {
      const secondLastMsg = messages[messages.length - 2]
      if (secondLastMsg.type === "user" && lastMsg.type === "assistant") {
        messageForActiveBlock1 = secondLastMsg
        messageForActiveBlock2 = lastMsg
        numMessagesInActiveBlock = 2
      } else {
        messageForActiveBlock1 = lastMsg
        numMessagesInActiveBlock = 1
      }
    }
  }

  const historicalMessagesToRender = messages.slice(
    0,
    messages.length - numMessagesInActiveBlock
  )

  const assistantInActiveBlockId =
    messageForActiveBlock2?.type === "assistant"
      ? messageForActiveBlock2?.id
      : messageForActiveBlock1?.type === "assistant"
      ? messageForActiveBlock1?.id
      : undefined

  const currentConversationId = data?.id
  const activeBlockKey = `msg1-${messageForActiveBlock1?.id ?? "none"}_msg2-${
    messageForActiveBlock2?.id ?? "none"
  }`

  const renderUserMessageNode = (
    msg: BrainSearchMessage,
    isPartOfActiveSection: boolean
  ) => {
    return (
      <div
        key={msg.id}
        id={`message-${msg.id}`}
        className={
          isPartOfActiveSection
            ? classes.userMessageInActiveContainer
            : classes.userMessageContainer
        }
      >
        <BrainSearchUserMessage brainSearchMessageKey={msg} />
      </div>
    )
  }

  const renderAssistantMessageNode = (
    msg: BrainSearchMessage,
    isPartOfActiveSection: boolean,
    isEffectivelyLastOverall: boolean,
    activeContext?: ActiveMessageContext
  ) => {
    const assistantWrapperClass = isPartOfActiveSection
      ? classes.assistantMessageInActiveContainer
      : classes.assistantMessageContainer

    if (
      isPartOfActiveSection &&
      activeContext?.isBotThinking &&
      msg.id === activeContext?.generatingMessageId
    ) {
      return (
        <div key={`${msg.id}-thinking`} className={assistantWrapperClass}>
          <div className={classes.thinkingStateUi}>
            <DiscoIcon
              icon={"stars"}
              width={16}
              height={16}
              color={"text.primary"}
              active
            />
            <DiscoText variant={"heading-xs-700"}>{"Generating response"}</DiscoText>
            <div className={classes.typingAnimation}>
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>
      )
    }

    return (
      <div key={msg.id} id={`message-${msg.id}`} className={assistantWrapperClass}>
        <BrainSearchAssistantMessage
          brainSearchMessageKey={msg}
          generatingMessageId={
            isPartOfActiveSection &&
            activeContext &&
            msg.id === activeContext.generatingMessageId
              ? activeContext.generatingMessageId
              : null
          }
          isLatestAssistantMessage={isEffectivelyLastOverall}
          onCreateMessage={activeContext?.onCreateMessage ?? onCreateMessage}
        />
      </div>
    )
  }

  useEffect(() => {
    // Effect 1: Scroll active generation spinner into view.
    if (
      isBotThinking &&
      generatingMessageId &&
      assistantInActiveBlockId &&
      generatingMessageId === assistantInActiveBlockId &&
      activeSectionRef.current
    ) {
      activeSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    }
  }, [isBotThinking, generatingMessageId, assistantInActiveBlockId])

  useEffect(() => {
    // Effect 2: Scroll to latest messages on initial load or conversation switch.
    const listElement = listContainerRef.current // Not in deps: stable ref, used for fallback scrollHeight
    const activeElement = activeSectionRef.current // Not in deps: stable ref, target of scroll

    // Reset scroll flag if conversation changes
    if (currentConversationId !== prevConversationIdRef.current) {
      conversationScrollDoneRef.current = false
      prevConversationIdRef.current = currentConversationId
    }

    // Scroll if: DOM ready, not generating, active block has content, conversation known, and not yet scrolled for this load.
    if (
      listElement &&
      activeElement &&
      !generatingMessageId &&
      (messageForActiveBlock1 || messageForActiveBlock2) &&
      currentConversationId &&
      !conversationScrollDoneRef.current
    ) {
      activeElement.scrollIntoView({ behavior: "auto", block: "start" })
      conversationScrollDoneRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId, generatingMessageId, activeBlockKey])

  return (
    <div ref={listContainerRef} className={classes.container}>
      <div className={classes.messageWrapper}>
        {historicalMessagesToRender.map((message) => {
          if (message.type === "user") {
            return renderUserMessageNode(message, false)
          }
          if (message.type === "assistant") {
            return renderAssistantMessageNode(message, false, false, {
              isBotThinking: false,
              generatingMessageId: null,
              onCreateMessage,
            })
          }
          return null
        })}

        {(messageForActiveBlock1 || messageForActiveBlock2) && (
          <div ref={activeSectionRef} className={classes.activeInteractionContainer}>
            <div className={classes.streamingStateUi}>
              {messageForActiveBlock1 &&
                messageForActiveBlock1.type === "user" &&
                renderUserMessageNode(messageForActiveBlock1, true)}
              {messageForActiveBlock1 &&
                messageForActiveBlock1.type === "assistant" &&
                renderAssistantMessageNode(
                  messageForActiveBlock1,
                  true,
                  !messageForActiveBlock2,
                  { isBotThinking, generatingMessageId, onCreateMessage }
                )}

              {messageForActiveBlock2 &&
                messageForActiveBlock2.type === "user" &&
                renderUserMessageNode(messageForActiveBlock2, true)}
              {messageForActiveBlock2 &&
                messageForActiveBlock2.type === "assistant" &&
                renderAssistantMessageNode(messageForActiveBlock2, true, true, {
                  isBotThinking,
                  generatingMessageId,
                  onCreateMessage,
                })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
type StyleProps = {
  paddingBottom: number
}

const useStyles = makeUseStyles((theme) => ({
  container: ({ paddingBottom }: StyleProps) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    height: "100%",
    overflowY: "auto",
    padding: theme.spacing(2, 2, paddingBottom, 2),
    gap: theme.spacing(3),
  }),
  messageWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    maxWidth: "720px",
    gap: theme.spacing(3),
    flexGrow: 0,
  },
  userMessageContainer: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
  },
  assistantMessageContainer: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
  },
  activeInteractionContainer: {
    minHeight: "90vh",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: theme.measure.borderRadius.medium,
  },
  thinkingStateUi: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    width: "100%",
  },
  streamingStateUi: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    width: "100%",
    padding: theme.spacing(2),
  },
  userMessageInActiveContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
  },
  assistantMessageInActiveContainer: {
    flexGrow: 1,
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  typingAnimation: {
    display: "inline-flex",
    alignItems: "center",
    height: "16px",
    "& span": {
      width: "4px",
      height: "4px",
      margin: "0 1px",
      backgroundColor: theme.palette.text.primary,
      borderRadius: "50%",
      display: "inline-block",
      animation: "$bounce 1.4s infinite ease-in-out both",
    },
    "& span:nth-child(1)": {
      animationDelay: "-0.32s",
    },
    "& span:nth-child(2)": {
      animationDelay: "-0.16s",
    },
  },
  // eslint-disable-next-line local-rules/disco-unused-classes
  "@keyframes bounce": {
    "0%, 80%, 100%": {
      transform: "scale(0)",
    },
    "40%": {
      transform: "scale(1)",
    },
  },
}))

export default Relay.withSkeleton({
  component: BrainSearchMessageList,
  skeleton: () => <DiscoSpinner absoluteCenter />,
})
